import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import moment from "moment";

const handleExport = async (
  data,
  fileName = "Exported_Data",
  districtData = [],
  statusData = [],
  fetchedUserDataRaw = []
) => {
  const fetchedUserData = fetchedUserDataRaw?.data || [];
  if (fileName !== "Form_Submissions_List_Data") {
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error("No data to export.");
      return;
    }
  } else {
    if (!data || !data.submissions || data.submissions.length === 0) {
      console.error("No submissions to export.");
      return;
    }
  }

  const exportFieldMap = {
    User_List_Data: ["user_name", "email", "phone", "district", "gender", "status", "date_of_join"],
    Leave_List_Data: [
      "user_name",
      "district_name",
      "leave_type",
      "start_date",
      "end_date",
      "reason",
      "status",
    ],
    Attendance_List_Data: [
      "user_name",
      "district_name",
      "attendance_date",
      "status",
      "leave_type",
      "reason",
      "logIn_time",
      "logOut_time",
    ],
    Attendance_List_Count_Data: [
      "user_name",
      "district_name",
      "no_of_days",
      "present_count",
      "leave_count",
      "absent_count",
      "leave_type",
      "reason",
    ],
    Attendance_List_User_Data: [
      "user_name",
      "district_name",
      "attendance_date",
      "status",
      "leave_type",
      "reason",
      "logIn_time",
      "logOut_time",
    ],
    Green_School_List_Data: [
      "group_name",
      "district_name",
      "green_fellow",
      "school_name",
      "agriculture_1",
      "agriculture_2",
      "agriculture_3",
      "horticulture_1",
      "horticulture_2",
      "hiring",
      "hiring_completed",
      "date_of_inspection",
      "remarks",
      "amount_received",
      "expenditure_incurred",
      "expenditure_to_be_incurred",
      "balance_available",
      "uc_status",
      "committed_expenditure",
      "unused_amount",
      "created_user",
      "updated_user",
      "created_at",
      "updated_at",
    ],
    Tickets_List_Data: [
      "user_name",
      "name",
      "ticket_subject",
      "ticket_description",
      "ticket_attachment",
      "prioritization",
      "status",
      "created_at",
    ],
    Activities_List_Data: [
      "mission_name",
      "activity_name",
      "status_name",
      "created_by_name",
      "updated_by_name",
      "created_at",
      "updated_at",
    ],
    Mission_List_Data: [
      "mission_name",
      "status_name",
      "created_by_name",
      "created_at",
      "updated_by_name",
      "updated_at",
    ],
    Claims_Records: [
      "user_name",
      "district_name",
      "month",
      "attendance_sheet",
      "stipend_claim",
      "travel_bills",
      "review_ta_bills",
      "created_at",
    ],
    Events_List_Data: ["event_name", "status", "created_at"],
    Event_Submissions_Export: [
      "event_name",
      "user_name",
      "district_name",
      "no_of_participants",
      "conducted_date",
      "description",
      "drive_url",
      "uploads",
      "created_at",
    ],
    Form_Submissions_List_Data: []
  };

  console.log("Fetched User Data for mapping:", fetchedUserData);

  // Mapping fetched data instead of constants
  const districtMap = Object.fromEntries((districtData || []).map((d) => [d.value, d.title]));
  const statusMap = Object.fromEntries((statusData || []).map((s) => [s.value, s.title]));
  const userMap = Object.fromEntries(
    (fetchedUserData || []).map((user) => [user.user_id, user.user_name])
  );

  console.log("User ID to Name map:", userMap);

  const formatHeader = (key) =>
    key
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  let formattedData = [];
  if (fileName === "Form_Submissions_List_Data") {
    const { id, title, description, created_by, created_user, createdAt, fields, submissions } = data;

    // Map field IDs to Labels
    const fieldMap = {};
    fields.forEach((f) => {
      fieldMap[f.id] = f.label;
    });

    // Format submissions
    formattedData = submissions.map((sub) => {
      const row = {
        "Form Title": title,
        "Form Description": description,
        "Form Created At": moment(createdAt).format("DD-MM-YYYY"),
        "Submitted By": sub.user_name || "-",
        "District": sub.district_name || "-",
        "Submission Date": moment(sub.created_at).format("DD-MM-YYYY"),
      };

      // Map dynamic field values
      Object.keys(sub).forEach((key) => {
        if (fieldMap[key]) {
          row[fieldMap[key]] = sub[key];
        }
      });

      if (sub.values) {
        Object.keys(sub.values).forEach((key) => {
          if (fieldMap[key]) {
            row[fieldMap[key]] = sub.values[key];
          }
        });
      }

      return row;
    });
  } else {
    const allowedFields = exportFieldMap[fileName];

    if (!allowedFields) {
      // If no specific mapping is found, use the data as-is (assuming it's already formatted)
      formattedData = data;
    } else {
      const customHeaders = {
        group_name: "Group",
        district_name: "District",
        green_fellow: "Name",
        school_name: "School Name",
        agriculture_1: "Solar Panel, Tubular battery/ Ongrid, lightings",
        agriculture_2: "Bore well, Drip Irrigation",
        agriculture_3: "Rain water Harvest",
        horticulture_1: "Compost",
        horticulture_2: "Vegetable Garden, Medicinal garden, Planting of fruit bearing trees",
        hiring: "Garden Maintenance",
        hiring_completed: "Completed Nos",
        date_of_inspection: "Tentative date of Inspection",
        remarks: "Remarks",
        amount_received: "Amount Received",
        expenditure_incurred: "Expenditure incurred",
        expenditure_to_be_incurred: "Expenditure to be incurred (Yet to pay)",
        balance_available: "Balance available",
        uc_status: "UC - Status",
        committed_expenditure: "Total Committed Expenditure(paid or yet to pay)in Rs.",
        unused_amount: "Total Unused Amount(expected or present)in Rs.",
        created_user: "Submitted By",
        updated_user: "Updated By",
        created_at: "Submitted At",
        updated_at: "Updated At",
        review_ta_bills: "Review TA Bills",
      };

      if (fileName === "Claims_Records") {
        customHeaders.month = "Claim Month";
        customHeaders.attendance_sheet = "Attendance Sheet";
        customHeaders.stipend_claim = "Stipend Claim";
        customHeaders.travel_bills = "Travel Bills";
        customHeaders.created_at = "Submitted On";
      }

      formattedData = data.map((row) => {
        const formattedRow = {};
        allowedFields.forEach((key) => {
          let value = row[key];

          if (fileName === "User_List_Data") {
            if (key === "district") {
              value = districtMap[value] || "-";
            } else if (key === "status") {
              value = statusMap[value] || "-";
            } else if (key === "date_of_join") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
          }
          if (fileName === "Events_List_Data") {
            if (key === "status") {
              value = statusMap[value] || value || "-";
            } else if (key === "created_at") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
          }
          if (fileName === "Event_Submissions_Export") {
            if (key === "conducted_date" || key === "created_at") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
            if (key === "uploads") {
              if (Array.isArray(value)) {
                value = value.join(",\n");
              } else if (typeof value === "string" && value.startsWith("[")) {
                try {
                  const parsed = JSON.parse(value);
                  if (Array.isArray(parsed)) value = parsed.join(",\n");
                } catch (e) {
                  // value remains as is
                }
              }
            }
          }
          if (fileName === "Leave_List_Data") {
            if (key === "start_date") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            } else if (key === "end_date") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
          }
          if (fileName === "Attendance_List_Data") {
            if (key === "attendance_date") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
            if (key === "logIn_time" || key === "logOut_time") {
              if (!value) {
                value = "00:00:00";
              } else {
                const [hour, minute, second] = value.split(":");
                const h = parseInt(hour, 10);
                const suffix = h >= 12 ? "PM" : "AM";
                const adjustedHour = ((h + 11) % 12) + 1;
                value = `${adjustedHour}:${minute}:${second} ${suffix}`;
              }
            }

            if (key === "leave_type" && (!value || value === "" || value === "On Duty")) {
              value = "-";
            }
          }
          if (fileName === "Attendance_List_User_Data") {
            if (key === "attendance_date") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
            if (key === "logIn_time" || key === "logOut_time") {
              if (!value) {
                value = "00:00:00";
              } else {
                const [hour, minute, second] = value.split(":");
                const h = parseInt(hour, 10);
                const suffix = h >= 12 ? "PM" : "AM";
                const adjustedHour = ((h + 11) % 12) + 1;
                value = `${adjustedHour}:${minute}:${second} ${suffix}`;
              }
            }

            if (key === "leave_type" && (!value || value === "" || value === "On Duty")) {
              value = "-";
            }
          }
          if (fileName === "Green_School_List_Data") {
            if (key === "created_by" || key === "updated_by") {
              value = userMap[value] || value || "-";
            } else if (key === "created_at" || key === "updated_at") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
          }
          if (fileName === "Tickets_List_Data") {
            if (key === "created_at") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
          }
          if (fileName === "Activities_List_Data") {
            if (key === "created_at" || key === "updated_at") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
          }
          if (fileName === "Mission_List_Data") {
            if (key === "created_at" || key === "updated_at") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
          }
          if (fileName === "Claims_Records") {
            if (key === "month") {
              value = value ? moment(value).format("MMM YYYY") : "-";
            }
            if (key === "created_at") {
              value = value ? moment(value).format("DD-MM-YYYY") : "-";
            }
            if (
              ["attendance_sheet", "stipend_claim", "travel_bills", "review_ta_bills"].includes(key)
            ) {
              const getAllUrls = (docData) => {
                if (!docData) return "-";
                let urls = [];
                try {
                  if (typeof docData === "string") {
                    if (docData.startsWith("[") && docData.endsWith("]")) {
                      urls = JSON.parse(docData);
                    } else {
                      urls = [docData];
                    }
                  } else if (Array.isArray(docData)) {
                    urls = docData;
                  }
                } catch (e) {
                  urls = [docData];
                }
                return urls.length > 0 ? urls.join(", ") : "-";
              };
              value = getAllUrls(value);
            }
          }

          const header = customHeaders[key] || formatHeader(key.replace(/^txt|^int/, ""));
          formattedRow[header] = value ?? "-";
        });
        return formattedRow;
      });
    }
  }

  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, `${fileName}_${moment().format("DD-MM-YYYY_hh-mm-a")}.xlsx`);
};

export default handleExport;
